import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Users, Calendar, DollarSign, TrendingUp, MessageSquare, User, LogOut, CheckCircle, XCircle, Eye, KeyRound, Trash2, BookOpen, Activity } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import ProfileForm from '../components/ProfileForm';
import GoalsSection from '../components/GoalsSection';
import AnalyticsSection from '../components/AnalyticsSection';
import ProfileAvatar from '../components/ProfileAvatar';
import NewsSection from '../components/NewsSection';
import KudosSection from '../components/KudosSection';
import BirthdaySection from '../components/BirthdaySection';
import AdminStudentsSection from '../components/AdminStudentsSection';
import AdminAttendanceSection from '../components/AdminAttendanceSection';
import AdminPaymentsSection from '../components/AdminPaymentsSection';
import AdminPayslipsSection from '../components/AdminPayslipsSection';
import CompanyInfoSection from '../components/CompanyInfoSection';
import PurchaseItemsSection from '../components/PurchaseItemsSection';
import NotificationBell from '../components/NotificationBell';
import useBirthdayGreeting from '../hooks/useBirthdayGreeting';
import FeedbackSection from '../components/FeedbackSection';
import CalendarEventsSection from '../components/CalendarEventsSection';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  useBirthdayGreeting(user);
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [students, setStudents] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [performances, setPerformances] = useState([]);
  const [claims, setClaims] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [forms, setForms] = useState([]);
  const [formProgress, setFormProgress] = useState([]);
  const [newForm, setNewForm] = useState({ title: '', url: '', description: '' });
  const [auditLogs, setAuditLogs] = useState([]);
  const [attendanceSettingsOpen, setAttendanceSettingsOpen] = useState(false);
  const [attendanceTargetUserId, setAttendanceTargetUserId] = useState(null);
  const [attendanceSettingsForm, setAttendanceSettingsForm] = useState({ expected_checkin_time: '', expected_checkout_time: '', location_lat: '', location_lng: '', allowed_radius_km: 1.0 });

  // Performance form
  const [perfForm, setPerfForm] = useState({
    user_id: '',
    review_period: '',
    rating: '',
    goals: '',
    achievements: '',
    areas_of_improvement: '',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        axios.get(`${API}/api/users`, { withCredentials: true }),
        axios.get(`${API}/api/students`, { withCredentials: true }),
        axios.get(`${API}/api/leaves`, { withCredentials: true }),
        axios.get(`${API}/api/leaves/balances`, { withCredentials: true }),
        axios.get(`${API}/api/performances`, { withCredentials: true }),
        axios.get(`${API}/api/claims`, { withCredentials: true }),
        axios.get(`${API}/api/feedback`, { withCredentials: true }),
        axios.get(`${API}/api/trainings`, { withCredentials: true }),
        axios.get(`${API}/api/forms`, { withCredentials: true }),
        axios.get(`${API}/api/forms/progress`, { withCredentials: true }),
        axios.get(`${API}/api/audit-logs`, { withCredentials: true }),
      ]);
      const [usersRes, studentsRes, leavesRes, leaveBalancesRes, perfsRes, claimsRes, feedbacksRes, trainingsRes, formsRes, progressRes, auditRes] = results;
      setUsers(usersRes.status === 'fulfilled' ? usersRes.value.data : []);
      setStudents(studentsRes.status === 'fulfilled' ? studentsRes.value.data : []);
      setLeaves(leavesRes.status === 'fulfilled' ? leavesRes.value.data : []);
      setLeaveBalances(leaveBalancesRes.status === 'fulfilled' ? leaveBalancesRes.value.data : []);
      setPerformances(perfsRes.status === 'fulfilled' ? perfsRes.value.data : []);
      setClaims(claimsRes.status === 'fulfilled' ? claimsRes.value.data : []);
      setFeedbacks(feedbacksRes.status === 'fulfilled' ? feedbacksRes.value.data : []);
      setTrainings(trainingsRes.status === 'fulfilled' ? trainingsRes.value.data : []);
      setForms(formsRes.status === 'fulfilled' ? (formsRes.value.data || []) : []);
      setFormProgress(progressRes.status === 'fulfilled' ? (progressRes.value.data || []) : []);
      setAuditLogs(auditRes.status === 'fulfilled' ? (auditRes.value.data || []) : []);
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length > 0) {
        console.error('Some dashboard data failed to load', failed);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLeaveUpdate = async (leaveId, status) => {
    try {
      await axios.patch(`${API}/api/leaves/${leaveId}`, { status }, { withCredentials: true });
      toast.success(`Leave ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update leave');
    }
  };

  const handleCreateForm = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/forms`, newForm, { withCredentials: true });
      toast.success('Form published');
      setNewForm({ title: '', url: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish form');
    }
  };

  const handleDeleteForm = async (formId, title) => {
    if (!window.confirm(`Delete published form '${title}'? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/forms/${formId}`, { withCredentials: true });
      toast.success('Form deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete form');
    }
  };

  const handleFormApproval = async (progressId, status) => {
    try {
      await axios.patch(`${API}/api/forms/progress/${progressId}`, { status }, { withCredentials: true });
      toast.success(`Form ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update form progress');
    }
  };

  const handleClaimUpdate = async (claimId, status) => {
    try {
      await axios.patch(`${API}/api/claims/${claimId}`, { status }, { withCredentials: true });
      toast.success(`Claim ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update claim');
    }
  };

  const handlePerformanceSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/performances`, { ...perfForm, rating: parseInt(perfForm.rating) }, { withCredentials: true });
      toast.success('Performance review created');
      setPerfForm({ user_id: '', review_period: '', rating: '', goals: '', achievements: '', areas_of_improvement: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create performance review');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    setPasswordSaving(true);
    try {
      await axios.post(
        `${API}/api/auth/change-password`,
        { current_password: passwordForm.current_password, new_password: passwordForm.new_password },
        { withCredentials: true }
      );
      toast.success('Password changed successfully');
      setChangePasswordOpen(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to change password';
      toast.error(typeof msg === 'string' ? msg : 'Failed to change password');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleAdminResetPassword = async (userId, userName) => {
    if (!window.confirm(`Reset password for ${userName}? A new temporary password will be generated.`)) return;
    try {
      const { data } = await axios.post(
        `${API}/api/auth/admin-reset-password/${userId}`,
        {},
        { withCredentials: true }
      );
      setResetResult(data);
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleDeleteUser = async (userId, userName, userRole) => {
    const label = userRole === 'parent' ? 'parent account (their children will remain in the system but detached)' : userRole === 'teacher' ? 'teacher account (they will be removed from all assigned students)' : 'user account';
    if (!window.confirm(`Delete ${userName}'s ${label}?\n\nThis will also remove their leaves, claims, goals, trainings, feedback and notifications. This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/users/${userId}`, { withCredentials: true });
      toast.success(`${userName} deleted`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-[#FEF3C7] text-[#F59E0B] border-[#F59E0B]',
      approved: 'bg-[#D1FAE5] text-[#10B981] border-[#10B981]',
      rejected: 'bg-[#FEE2E2] text-[#EF4444] border-[#EF4444]',
    };
    return <Badge className={`${variants[status]} border`} data-testid={`status-badge-${status}`}>{status.charAt(0).toUpperCase() + status.slice(1)}</Badge>;
  };

  const stats = {
    totalTeachers: users.filter((u) => u.role === 'teacher').length,
    pendingLeaves: leaves.filter((l) => l.status === 'pending').length,
    pendingClaims: claims.filter((c) => c.status === 'pending').length,
    totalFeedback: feedbacks.length,
  };

  const parentChildrenMap = students.reduce((acc, student) => {
    if (student.parent_user_id) {
      acc[student.parent_user_id] = [...(acc[student.parent_user_id] || []), student.name];
    }
    return acc;
  }, {});

  const parentUsers = users.filter((u) => u.role === 'parent');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F5]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F4F5]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_teach-track-48/artifacts/r4w4bz0k_IMG_20260701_210749.png" 
                alt="SSTC Logo" 
                className="w-full h-full object-contain"
                data-testid="sstc-logo-header"
              />
            </div>
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                SSTC e-Management System
              </h1>
              <p className="text-xs text-slate-500">Admin Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <NotificationBell />
            <div className="text-right">
              <p className="text-sm font-medium text-slate-900" data-testid="admin-name">{user?.name}</p>
              <p className="text-xs text-slate-500" data-testid="admin-role">Administrator</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setChangePasswordOpen(true)}
              data-testid="admin-change-password-button"
              title="Change Password"
            >
              <KeyRound className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="admin-logout-button">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
          <Card className="bg-white border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200" data-testid="teachers-stats-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-5 h-5 text-blue-600" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Teachers</span>
              </div>
              <p className="text-3xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{stats.totalTeachers}</p>
              <p className="text-sm text-slate-600 mt-1">Total staff members</p>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200" data-testid="leaves-stats-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Leaves</span>
              </div>
              <p className="text-3xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{stats.pendingLeaves}</p>
              <p className="text-sm text-slate-600 mt-1">Pending approvals</p>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200" data-testid="claims-stats-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-5 h-5 text-blue-600" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Claims</span>
              </div>
              <p className="text-3xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{stats.pendingClaims}</p>
              <p className="text-sm text-slate-600 mt-1">Pending claims</p>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200" data-testid="feedback-stats-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <MessageSquare className="w-5 h-5 text-blue-600" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Feedback</span>
              </div>
              <p className="text-3xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{stats.totalFeedback}</p>
              <p className="text-sm text-slate-600 mt-1">Total feedback</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="news" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 flex-wrap h-auto" data-testid="admin-tabs">
            <TabsTrigger value="news" data-testid="tab-news">News & Announcements</TabsTrigger>
            <TabsTrigger value="kudos" data-testid="tab-kudos">Kudos</TabsTrigger>
            <TabsTrigger value="staff" data-testid="tab-staff">Staff</TabsTrigger>
            <TabsTrigger value="attendance" data-testid="tab-attendance">Attendance</TabsTrigger>
            <TabsTrigger value="parents" data-testid="tab-parents">Parents</TabsTrigger>
            <TabsTrigger value="students" data-testid="tab-students">Students</TabsTrigger>
            <TabsTrigger value="finance" data-testid="tab-finance">Finance</TabsTrigger>
            <TabsTrigger value="purchase-items" data-testid="tab-purchase-items">Purchase Items</TabsTrigger>
            <TabsTrigger value="leaves" data-testid="tab-leaves">Leaves</TabsTrigger>
            <TabsTrigger value="leave-history" data-testid="tab-leave-history">Leave History</TabsTrigger>
            <TabsTrigger value="claims" data-testid="tab-claims">Claims</TabsTrigger>
            <TabsTrigger value="performance" data-testid="tab-performance">Performance</TabsTrigger>
            <TabsTrigger value="trainings" data-testid="tab-trainings">Trainings</TabsTrigger>
            <TabsTrigger value="forms" data-testid="tab-forms">Forms</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">Audit</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="feedback" data-testid="tab-feedback">Feedback</TabsTrigger>
          </TabsList>

          {/* Students Tab */}
          <TabsContent value="students">
            <AdminStudentsSection />
          </TabsContent>

          {/* Finance Tab */}
          <TabsContent value="finance">
            <div className="space-y-6">
              <AdminPaymentsSection />
              <AdminPayslipsSection />
              <CompanyInfoSection />
            </div>
          </TabsContent>

          {/* Purchase Items Tab */}
          <TabsContent value="purchase-items">
            <PurchaseItemsSection />
          </TabsContent>

          {/* News Tab */}
          <TabsContent value="news">
            <div className="space-y-6">
              <NewsSection isAdmin={true} />
              <BirthdaySection />
              <CalendarEventsSection isAdmin={true} />
            </div>
          </TabsContent>

          {/* Kudos Tab */}
          <TabsContent value="kudos">
            <div className="space-y-6">
              <KudosSection isAdmin={true} />
            </div>
          </TabsContent>

          {/* Forms Tab */}
          <TabsContent value="forms">
            <div className="space-y-6">
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Managed Forms</CardTitle>
                  <CardDescription>Create and manage links for teachers</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Title</TableHead>
                          <TableHead>URL</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody data-testid="admin-forms-table">
                        {forms.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center text-slate-500">No forms published yet</TableCell>
                          </TableRow>
                        ) : (
                          forms.map((form, idx) => (
                            <TableRow key={form.id} data-testid={`admin-form-row-${idx}`}>
                              <TableCell>{form.title}</TableCell>
                              <TableCell>
                                <Button
                                  size="sm"
                                  className="bg-slate-100 text-slate-700 hover:bg-slate-200"
                                  disabled={!form.url}
                                  onClick={() => form.url && window.open(form.url, '_blank', 'noopener,noreferrer')}
                                  data-testid={`open-form-${form.id}`}
                                >
                                  <Eye className="w-4 h-4 mr-2 inline" />
                                  Open
                                </Button>
                              </TableCell>
                              <TableCell>{form.description || '—'}</TableCell>
                              <TableCell>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600"
                                  onClick={() => handleDeleteForm(form.id, form.title)}
                                  data-testid={`delete-form-${form.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Publish New Form</CardTitle>
                  <CardDescription>Share a new form link with teachers</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCreateForm} className="space-y-4" data-testid="create-form-form">
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={newForm.title}
                        onChange={(e) => setNewForm({ ...newForm, title: e.target.value })}
                        required
                        data-testid="new-form-title-input"
                      />
                    </div>
                    <div>
                      <Label>URL</Label>
                      <Input
                        value={newForm.url}
                        onChange={(e) => setNewForm({ ...newForm, url: e.target.value })}
                        required
                        data-testid="new-form-url-input"
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={newForm.description}
                        onChange={(e) => setNewForm({ ...newForm, description: e.target.value })}
                        rows={3}
                        data-testid="new-form-description-input"
                      />
                    </div>
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="create-form-button">
                      Publish Form
                    </Button>
                  </form>
                </CardContent>
              </Card>
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Submitted Form Progress</CardTitle>
                  <CardDescription>Review and approve teacher submissions</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Teacher</TableHead>
                          <TableHead>Form</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Updated</TableHead>
                          <TableHead>Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody data-testid="form-progress-table">
                        {formProgress.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center text-slate-500">No form progress submitted yet</TableCell>
                          </TableRow>
                        ) : (
                          formProgress.map((progress, idx) => (
                            <TableRow key={progress.id} data-testid={`form-progress-row-${idx}`}>
                              <TableCell>{progress.user_name || 'Unknown'}</TableCell>
                              <TableCell>{progress.form_title || progress.form_id}</TableCell>
                              <TableCell>{getStatusBadge(progress.status)}</TableCell>
                              <TableCell className="text-sm text-slate-600">{new Date(progress.updated_at).toLocaleString()}</TableCell>
                              <TableCell>
                                {progress.status === 'submitted' ? (
                                  <div className="flex gap-2 flex-wrap">
                                    <Button size="sm" className="bg-[#10B981] hover:bg-[#059669] text-white" onClick={() => handleFormApproval(progress.id, 'approved')} data-testid={`approve-form-progress-${idx}`}>
                                      <CheckCircle className="w-4 h-4" />
                                    </Button>
                                    <Button size="sm" className="bg-[#EF4444] hover:bg-[#DC2626] text-white" onClick={() => handleFormApproval(progress.id, 'rejected')} data-testid={`reject-form-progress-${idx}`}>
                                      <XCircle className="w-4 h-4" />
                                    </Button>
                                  </div>
                                ) : (
                                  <span className="text-xs text-slate-500">No review needed</span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Audit Tab */}
          <TabsContent value="audit">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}><Activity className="w-5 h-5 inline mr-2" />Audit Trail</CardTitle>
                <CardDescription>Recent admin, teacher and system actions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>When</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Action</TableHead>
                        <TableHead>Target</TableHead>
                        <TableHead>Details</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody data-testid="audit-logs-table">
                      {auditLogs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-slate-500">No audit entries found</TableCell>
                        </TableRow>
                      ) : (
                        auditLogs.map((entry, idx) => (
                          <TableRow key={entry.id || idx} data-testid={`audit-log-row-${idx}`}>
                            <TableCell className="text-sm text-slate-600">{entry.created_at ? new Date(entry.created_at).toLocaleString() : '—'}</TableCell>
                            <TableCell>{entry.user_name || 'System'}</TableCell>
                            <TableCell>{entry.role || '—'}</TableCell>
                            <TableCell className="font-medium text-slate-900">{entry.action}</TableCell>
                            <TableCell>{entry.target_type ? `${entry.target_type}/${entry.target_id || '—'}` : '—'}</TableCell>
                            <TableCell className="text-sm text-slate-600 max-w-xs break-words">{entry.details ? JSON.stringify(entry.details) : '—'}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <AnalyticsSection />
          </TabsContent>

          {/* Attendance Tab */}
          <TabsContent value="attendance">
            <AdminAttendanceSection />
          </TabsContent>

          {/* Staff Tab */}
          <TabsContent value="staff">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Staff Overview</CardTitle>
                <CardDescription>View all staff members</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Department</TableHead>
                        <TableHead>Employee ID</TableHead>
                        <TableHead>Annual Leave</TableHead>
                        <TableHead>Sick Leave</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody data-testid="staff-table">
                      {(() => {
                        const staff = users.filter((x) => x.role === 'admin' || x.role === 'teacher');
                        if (staff.length === 0) {
                          return (
                            <TableRow>
                              <TableCell colSpan={7} className="text-center text-slate-500">No staff accounts found</TableCell>
                            </TableRow>
                          );
                        }
                        return staff.map((u, idx) => (
                          <TableRow key={u.id} data-testid={`staff-row-${idx}`}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-3">
                              <ProfileAvatar userId={u.id} size="sm" testId={`staff-avatar-${idx}`} />
                              <span>{u.name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                          <TableCell className="text-sm text-slate-600">{u.department || 'N/A'}</TableCell>
                          <TableCell className="text-sm text-slate-600">{u.employee_id || 'N/A'}</TableCell>
                          {(() => {
                            const balance = leaveBalances.find((lb) => lb.user_id === u.id);
                            return (
                              <>
                                <TableCell className="text-sm text-slate-600">
                                  {balance
                                    ? `${balance.annual_leave_remaining.toFixed(2)} / ${balance.annual_leave_earned.toFixed(2)} earned`
                                    : typeof u.annual_leave_allowance === 'number'
                                      ? `${Math.max(0, (u.annual_leave_allowance || 0) - (u.annual_leave_used || 0))} / ${u.annual_leave_allowance}`
                                      : 'N/A'}
                                </TableCell>
                                <TableCell className="text-sm text-slate-600">
                                  {balance
                                    ? `${balance.sick_leave_remaining.toFixed(2)} / ${balance.sick_leave_allowance.toFixed(2)}`
                                    : typeof u.sick_leave_allowance === 'number'
                                      ? `${Math.max(0, (u.sick_leave_allowance || 0) - (u.sick_leave_used || 0))} / ${u.sick_leave_allowance}`
                                      : 'N/A'}
                                </TableCell>
                              </>
                            );
                          })()}
                          <TableCell>
                            <Badge className={u.role === 'admin' ? 'bg-blue-100 text-blue-700 border-blue-300 border' : 'bg-slate-100 text-slate-700 border-slate-300 border'}>
                              {u.role}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedUserId(u.id);
                                  setShowProfileDialog(true);
                                }}
                                data-testid={`view-profile-${idx}`}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View/Edit
                              </Button>
                              <Button size="sm" variant="outline" onClick={async () => {
                                  setAttendanceTargetUserId(u.id);
                                  try {
                                    const { data } = await axios.get(`${API}/api/attendance/settings/${u.id}`, { withCredentials: true });
                                    setAttendanceSettingsForm({
                                      expected_checkin_time: data?.expected_checkin_time || '',
                                      expected_checkout_time: data?.expected_checkout_time || '',
                                      location_lat: data?.location_lat ?? '',
                                      location_lng: data?.location_lng ?? '',
                                      allowed_radius_km: data?.allowed_radius_km ?? 1.0,
                                    });
                                  } catch (err) {
                                    setAttendanceSettingsForm({ expected_checkin_time: '', expected_checkout_time: '', location_lat: '', location_lng: '', allowed_radius_km: 1.0 });
                                  }
                                  setAttendanceSettingsOpen(true);
                                }} data-testid={`attendance-settings-${idx}`}>Attendance</Button>
                              {u.role === 'teacher' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleAdminResetPassword(u.id, u.name)}
                                  className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                  data-testid={`reset-password-${idx}`}
                                  title="Reset Password"
                                >
                                  <KeyRound className="w-4 h-4" />
                                </Button>
                              )}
                              {u.id !== user?.id && u.email !== 'admin@teachtrack.com' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeleteUser(u.id, u.name, u.role)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-user-${idx}`}
                                  title="Delete Account"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                          </TableRow>
                        ));
                      })()}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Parents Tab */}
          <TabsContent value="parents">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Parent Accounts</CardTitle>
                <CardDescription>View parent accounts and their registered children</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Children</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody data-testid="parent-accounts-table">
                      {parentUsers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-slate-500">No parent accounts found</TableCell>
                        </TableRow>
                      ) : (
                        parentUsers.map((u, idx) => (
                          <TableRow key={u.id} data-testid={`parent-account-row-${idx}`}>
                            <TableCell className="font-medium">{u.name}</TableCell>
                            <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                            <TableCell className="text-sm text-slate-600">
                              {parentChildrenMap[u.id]?.length > 0 ? parentChildrenMap[u.id].join(', ') : 'No children'}
                            </TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteUser(u.id, u.name, u.role)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-parent-${idx}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Leave Approvals Tab */}
          <TabsContent value="leaves">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Leave Applications</CardTitle>
                <CardDescription>Review and approve leave requests</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Teacher</TableHead>
                        <TableHead>Leave Type</TableHead>
                        <TableHead>Dates</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody data-testid="leave-approvals-table">
                      {leaves.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-slate-500">No leave applications</TableCell>
                        </TableRow>
                      ) : (
                        leaves.map((leave, idx) => (
                          <TableRow key={leave.id} data-testid={`leave-approval-row-${idx}`}>
                            <TableCell className="font-medium">{leave.user_name}</TableCell>
                            <TableCell className="text-sm text-slate-600">{leave.leave_type.replace('_', ' ').toUpperCase()}</TableCell>
                            <TableCell className="text-sm text-slate-600">{leave.start_date} to {leave.end_date}</TableCell>
                            <TableCell className="text-sm text-slate-600 max-w-xs truncate">{leave.reason}</TableCell>
                            <TableCell>{getStatusBadge(leave.status)}</TableCell>
                            <TableCell>
                              {leave.status === 'pending' ? (
                                <div className="flex gap-2">
                                  <Button 
                                    size="sm" 
                                    className="bg-[#10B981] hover:bg-[#059669] text-white" 
                                    onClick={() => handleLeaveUpdate(leave.id, 'approved')}
                                    data-testid={`approve-leave-${idx}`}
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    className="bg-[#EF4444] hover:bg-[#DC2626] text-white" 
                                    onClick={() => handleLeaveUpdate(leave.id, 'rejected')}
                                    data-testid={`reject-leave-${idx}`}
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                </div>
                              ) : (
                                <span className="text-xs text-slate-500">—</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Claim Approvals Tab */}
          <TabsContent value="claims">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Claim Requests</CardTitle>
                <CardDescription>Review and approve claims</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Teacher</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody data-testid="claim-approvals-table">
                      {claims.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-slate-500">No claims submitted</TableCell>
                        </TableRow>
                      ) : (
                        claims.map((claim, idx) => (
                          <TableRow key={claim.id} data-testid={`claim-approval-row-${idx}`}>
                            <TableCell className="font-medium">{claim.user_name}</TableCell>
                            <TableCell className="text-sm text-slate-600">{claim.claim_type.toUpperCase()}</TableCell>
                            <TableCell className="text-right font-medium">${claim.amount.toFixed(2)}</TableCell>
                            <TableCell className="text-sm text-slate-600 max-w-xs">
                              <div className="truncate">{claim.description}</div>
                              {claim.receipt_url && (
                                <a
                                  href={`${API}/api/files/${claim.receipt_url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:text-blue-700 underline"
                                  data-testid={`claim-receipt-link-${idx}`}
                                >
                                  View Receipt
                                </a>
                              )}
                            </TableCell>
                            <TableCell>{getStatusBadge(claim.status)}</TableCell>
                            <TableCell>
                              {claim.status === 'pending' ? (
                                <div className="flex gap-2">
                                  <Button 
                                    size="sm" 
                                    className="bg-[#10B981] hover:bg-[#059669] text-white" 
                                    onClick={() => handleClaimUpdate(claim.id, 'approved')}
                                    data-testid={`approve-claim-${idx}`}
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    className="bg-[#EF4444] hover:bg-[#DC2626] text-white" 
                                    onClick={() => handleClaimUpdate(claim.id, 'rejected')}
                                    data-testid={`reject-claim-${idx}`}
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                </div>
                              ) : (
                                <span className="text-xs text-slate-500">—</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Leave History Tab */}
          <TabsContent value="leave-history">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Leave History</CardTitle>
                <CardDescription>Complete record of all teacher leaves and approvals</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <label className="text-sm font-medium text-slate-700 mr-3">Filter by Year:</label>
                  <select 
                    defaultValue={new Date().getFullYear()}
                    onChange={(e) => {
                      // This could trigger a refetch with selected year
                      window.location.href = `?year=${e.target.value}`;
                    }}
                    className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                  >
                    {[new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Teacher</TableHead>
                        <TableHead>Leave Type</TableHead>
                        <TableHead>From</TableHead>
                        <TableHead>To</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Submitted</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody data-testid="leave-history-table">
                      {leaves.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-slate-500">No leave records</TableCell>
                        </TableRow>
                      ) : (
                        leaves
                          .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
                          .map((leave, idx) => (
                            <TableRow key={leave.id} data-testid={`leave-history-row-${idx}`}>
                              <TableCell className="font-medium">{leave.user_name}</TableCell>
                              <TableCell className="text-sm text-slate-600">{leave.leave_type.replace('_', ' ').toUpperCase()}</TableCell>
                              <TableCell className="text-sm text-slate-600">{leave.start_date}</TableCell>
                              <TableCell className="text-sm text-slate-600">{leave.end_date}</TableCell>
                              <TableCell className="text-sm text-slate-600 max-w-xs truncate">{leave.reason}</TableCell>
                              <TableCell>{getStatusBadge(leave.status)}</TableCell>
                              <TableCell className="text-xs text-slate-500">
                                {leave.created_at ? new Date(leave.created_at).toLocaleDateString('en-MY') : '—'}
                              </TableCell>
                            </TableRow>
                          ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance" className="space-y-6">
            <GoalsSection isAdmin={true} />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2 bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Performance Reviews</CardTitle>
                  <CardDescription>All performance reviews</CardDescription>
                </CardHeader>
                <CardContent>
                  {performances.length === 0 ? (
                    <div className="text-center py-12 text-slate-500" data-testid="no-performance-reviews">
                      <TrendingUp className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                      <p>No performance reviews yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4" data-testid="performance-reviews-list">
                      {performances.map((perf, idx) => (
                        <div key={perf.id} className="p-4 border border-slate-200 rounded-lg" data-testid={`performance-review-${idx}`}>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-slate-900">{perf.user_name}</h4>
                            <Badge className="bg-blue-100 text-blue-700 border-blue-300 border">Rating: {perf.rating}/5</Badge>
                          </div>
                          <p className="text-sm text-slate-600 mb-2">Period: {perf.review_period}</p>
                          <div className="text-xs text-slate-700">
                            <p><strong>Goals:</strong> {perf.goals}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Create Review</CardTitle>
                  <CardDescription>Add performance review</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handlePerformanceSubmit} className="space-y-4" data-testid="create-performance-form">
                    <div>
                      <Label htmlFor="user_id">Teacher</Label>
                      <Select value={perfForm.user_id} onValueChange={(val) => setPerfForm({ ...perfForm, user_id: val })}>
                        <SelectTrigger data-testid="performance-teacher-select">
                          <SelectValue placeholder="Select teacher" />
                        </SelectTrigger>
                        <SelectContent>
                          {users.filter(u => u.role === 'teacher').map((u, idx) => (
                            <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="review_period">Review Period</Label>
                      <Input
                        value={perfForm.review_period}
                        onChange={(e) => setPerfForm({ ...perfForm, review_period: e.target.value })}
                        required
                        placeholder="e.g., Q1 2024"
                        data-testid="performance-period-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="rating">Rating (1-5)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="5"
                        value={perfForm.rating}
                        onChange={(e) => setPerfForm({ ...perfForm, rating: e.target.value })}
                        required
                        data-testid="performance-rating-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="goals">Goals</Label>
                      <Textarea
                        value={perfForm.goals}
                        onChange={(e) => setPerfForm({ ...perfForm, goals: e.target.value })}
                        required
                        data-testid="performance-goals-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="achievements">Achievements</Label>
                      <Textarea
                        value={perfForm.achievements}
                        onChange={(e) => setPerfForm({ ...perfForm, achievements: e.target.value })}
                        required
                        data-testid="performance-achievements-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="areas_of_improvement">Areas of Improvement</Label>
                      <Textarea
                        value={perfForm.areas_of_improvement}
                        onChange={(e) => setPerfForm({ ...perfForm, areas_of_improvement: e.target.value })}
                        required
                        data-testid="performance-improvement-input"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-performance-button">
                      Create Review
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Trainings Tab */}
          <TabsContent value="trainings">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>All Trainings</CardTitle>
                <CardDescription>Training records from all teachers</CardDescription>
              </CardHeader>
              <CardContent>
                {trainings.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                    <p>No training records yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Teacher</TableHead>
                          <TableHead>Title</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Duration</TableHead>
                          <TableHead>Certificate</TableHead>
                          <TableHead>Date Added</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody data-testid="admin-trainings-table">
                        {trainings.map((training, idx) => (
                          <TableRow key={training.id} data-testid={`admin-training-row-${idx}`}>
                            <TableCell className="font-medium">{training.user_name}</TableCell>
                            <TableCell>{training.title}</TableCell>
                            <TableCell className="max-w-xs truncate">{training.description}</TableCell>
                            <TableCell>{training.date}</TableCell>
                            <TableCell>{training.duration}</TableCell>
                            <TableCell>
                              {training.certificate_url ? (
                                <a
                                  href={`${API}/api/files/${training.certificate_url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-700 underline"
                                  data-testid={`admin-training-certificate-${idx}`}
                                >
                                  View
                                </a>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-slate-600">
                              {training.created_at ? new Date(training.created_at).toLocaleDateString() : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Feedback Tab */}
          <TabsContent value="feedback">
            <FeedbackSection />
          </TabsContent>
        </Tabs>
      </div>

      {/* Profile View/Edit Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="profile-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Teacher Profile</DialogTitle>
            <DialogDescription>View and update this teacher's profile information</DialogDescription>
          </DialogHeader>
          {selectedUserId && (
            <ProfileForm
              userId={selectedUserId}
              viewOnly={false}
              onSaved={() => {
                fetchData();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Attendance Settings Dialog */}
      <Dialog open={attendanceSettingsOpen} onOpenChange={() => setAttendanceSettingsOpen(false)}>
        <DialogContent className="max-w-md" data-testid="attendance-settings-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Attendance Settings</DialogTitle>
            <DialogDescription>Set expected check-in/out times and allowed location radius for the teacher</DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={async (e) => {
            e.preventDefault();
            try {
              await axios.put(`${API}/api/attendance/settings/${attendanceTargetUserId}`, {
                user_id: attendanceTargetUserId,
                expected_checkin_time: attendanceSettingsForm.expected_checkin_time,
                expected_checkout_time: attendanceSettingsForm.expected_checkout_time,
                location_lat: attendanceSettingsForm.location_lat ? parseFloat(attendanceSettingsForm.location_lat) : null,
                location_lng: attendanceSettingsForm.location_lng ? parseFloat(attendanceSettingsForm.location_lng) : null,
                allowed_radius_km: parseFloat(attendanceSettingsForm.allowed_radius_km) || 1.0,
              }, { withCredentials: true });
              toast.success('Attendance settings saved');
              setAttendanceSettingsOpen(false);
            } catch (err) {
              toast.error(err.response?.data?.detail || 'Failed to save settings');
            }
          }}>
            <div>
              <Label>Expected Check-in Time (HH:MM)</Label>
              <Input value={attendanceSettingsForm.expected_checkin_time} onChange={(e) => setAttendanceSettingsForm({ ...attendanceSettingsForm, expected_checkin_time: e.target.value })} placeholder="08:30" />
            </div>
            <div>
              <Label>Expected Check-out Time (HH:MM)</Label>
              <Input value={attendanceSettingsForm.expected_checkout_time} onChange={(e) => setAttendanceSettingsForm({ ...attendanceSettingsForm, expected_checkout_time: e.target.value })} placeholder="17:00" />
            </div>
            <div>
              <Label>Location Latitude</Label>
              <Input value={attendanceSettingsForm.location_lat} onChange={(e) => setAttendanceSettingsForm({ ...attendanceSettingsForm, location_lat: e.target.value })} placeholder="3.12345" />
            </div>
            <div>
              <Label>Location Longitude</Label>
              <Input value={attendanceSettingsForm.location_lng} onChange={(e) => setAttendanceSettingsForm({ ...attendanceSettingsForm, location_lng: e.target.value })} placeholder="101.12345" />
            </div>
            <div>
              <Label>Allowed Radius (km)</Label>
              <Input type="number" step="0.1" value={attendanceSettingsForm.allowed_radius_km} onChange={(e) => setAttendanceSettingsForm({ ...attendanceSettingsForm, allowed_radius_km: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setAttendanceSettingsOpen(false)}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" type="submit">Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog (admin's own) */}
      <Dialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen}>
        <DialogContent className="max-w-md" data-testid="admin-change-password-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Change Password</DialogTitle>
            <DialogDescription>Enter your current password and choose a new one (min 6 characters)</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4" data-testid="admin-change-password-form">
            <div>
              <Label htmlFor="a_current_password">Current Password</Label>
              <Input
                id="a_current_password"
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                required
                data-testid="admin-current-password-input"
              />
            </div>
            <div>
              <Label htmlFor="a_new_password">New Password</Label>
              <Input
                id="a_new_password"
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                required
                minLength={6}
                data-testid="admin-new-password-input"
              />
            </div>
            <div>
              <Label htmlFor="a_confirm_password">Confirm New Password</Label>
              <Input
                id="a_confirm_password"
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                required
                minLength={6}
                data-testid="admin-confirm-password-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <Button type="button" variant="outline" onClick={() => setChangePasswordOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={passwordSaving} className="bg-blue-600 hover:bg-blue-700" data-testid="admin-save-password-button">
                {passwordSaving ? 'Saving...' : 'Change Password'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Admin Reset Password Result Dialog */}
      <Dialog open={!!resetResult} onOpenChange={(open) => !open && setResetResult(null)}>
        <DialogContent className="max-w-md" data-testid="reset-result-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Password Reset Complete</DialogTitle>
            <DialogDescription>Share this temporary password with the user securely. It will only be shown once.</DialogDescription>
          </DialogHeader>
          {resetResult && (
            <div className="space-y-4">
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">User</p>
                <p className="text-sm text-slate-700 mb-3">{resetResult.user_email}</p>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Temporary Password</p>
                <p className="text-lg font-mono font-semibold text-slate-900 bg-white px-3 py-2 rounded border border-slate-200 select-all" data-testid="temp-password-display">
                  {resetResult.temporary_password}
                </p>
              </div>
              <p className="text-xs text-slate-500">
                Instruct the user to log in with this password and change it immediately.
              </p>
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
                <Button
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard?.writeText(resetResult.temporary_password);
                    toast.success('Password copied to clipboard');
                  }}
                  data-testid="copy-password-button"
                >
                  Copy Password
                </Button>
                <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setResetResult(null)} data-testid="close-reset-result-button">
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
